"use client";
import { RefObject, useCallback, useEffect, useState } from "react";

import { Icon } from "@zapier/zinnia-icons";
import {
  Spinner,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";

import FitViewIcon from "./FitViewIcon";
import styles from "./CanvasEmbedZoomControls.module.css";

function toPercentage(decimal: number) {
  return `${Math.floor(decimal * 100)}%`;
}

type ZoomLevel = 0.25 | 0.5 | 0.75 | 1 | 1.5 | 2;
const ZOOM_LEVELS: ZoomLevel[] = [0.25, 0.5, 0.75, 1, 1.5, 2];
const MAX_ZOOM = ZOOM_LEVELS.at(-1);
const MIN_ZOOM = ZOOM_LEVELS[0];

interface Props {
  iframeRef: RefObject<HTMLIFrameElement>;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

export function CanvasEmbedZoomControls({
  iframeRef,
  isExpanded = false,
  onToggleExpand,
}: Props) {
  const t = useTranslations("folder-documentation");
  const [zoom, setZoom] = useState<number | undefined>();

  useEffect(() => {
    const handleMessageFromCanvas = (event: MessageEvent) => {
      if (event.data.type === "zoomLevel") {
        setZoom(event.data.zoom);
      }
    };

    window.addEventListener("message", handleMessageFromCanvas);

    return () => window.removeEventListener("message", handleMessageFromCanvas);
  }, []);

  const zoomToFit = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ action: "zoomToFit" }, "*");
    }
  }, [iframeRef]);

  const handleZoomOut = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ action: "zoomOut" }, "*");
    }
  }, [iframeRef]);

  const handleZoomIn = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ action: "zoomIn" }, "*");
    }
  }, [iframeRef]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.zoomControls}>
        {zoom === MIN_ZOOM ? (
          <button
            type="button"
            aria-label={t("zoomControls.zoomOut")}
            className={styles.zoomOutButton}
            onClick={handleZoomOut}
            disabled
          >
            <Icon name="formDash" size={16} color="GrayWarm9" />
          </button>
        ) : (
          <Tooltip content={t("zoomControls.zoomOut")} placement="top">
            <TooltipFocusableTrigger>
              <button
                type="button"
                aria-label={t("zoomControls.zoomOut")}
                className={styles.zoomOutButton}
                onClick={handleZoomOut}
              >
                <Icon name="formDash" size={16} color="GrayWarm9" />
              </button>
            </TooltipFocusableTrigger>
          </Tooltip>
        )}

        <div className={styles.zoomNumber}>
          {zoom === undefined ? <Spinner /> : <Text>{toPercentage(zoom)}</Text>}
        </div>

        {zoom === MAX_ZOOM ? (
          <button
            type="button"
            aria-label={t("zoomControls.zoomIn")}
            className={styles.zoomInButton}
            onClick={handleZoomIn}
            disabled
          >
            <Icon name="formAdd" size={16} color="GrayWarm9" />
          </button>
        ) : (
          <Tooltip content={t("zoomControls.zoomIn")} placement="top">
            <TooltipFocusableTrigger>
              <button
                type="button"
                aria-label={t("zoomControls.zoomIn")}
                className={styles.zoomInButton}
                onClick={handleZoomIn}
              >
                <Icon name="formAdd" size={16} color="GrayWarm9" />
              </button>
            </TooltipFocusableTrigger>
          </Tooltip>
        )}
      </div>
      <Tooltip content={t("zoomControls.zoomToFit")} placement="top">
        <TooltipFocusableTrigger>
          <button
            type="button"
            aria-label={t("zoomControls.zoomToFit")}
            className={styles.fitViewButton}
            onClick={zoomToFit}
          >
            <FitViewIcon />
          </button>
        </TooltipFocusableTrigger>
      </Tooltip>
      {onToggleExpand && (
        <Tooltip
          content={
            isExpanded
              ? t("zoomControls.collapseEmbed")
              : t("zoomControls.expandEmbed")
          }
          placement="top"
        >
          <TooltipFocusableTrigger>
            <button
              type="button"
              aria-label={
                isExpanded
                  ? t("zoomControls.collapseEmbed")
                  : t("zoomControls.expandEmbed")
              }
              className={styles.expandButton}
              onClick={onToggleExpand}
            >
              <Icon
                name={isExpanded ? "arrowContract" : "arrowExpand"}
                size={16}
                color="GrayWarm9"
              />
            </button>
          </TooltipFocusableTrigger>
        </Tooltip>
      )}
    </div>
  );
}
